import { PollingController, type PollingSnapshot } from "@/lifecycle";
import { createLogger } from "@/logging";

const logger = createLogger("components/base/appUpdate");

// 배포는 하루 몇 번이므로 분 단위 주기면 충분하다. 탭이 다시 보이면 PollingController 가 즉시
// 한 번 읽는다. 그래서 자리를 비운 동안의 배포는 복귀할 때 바로 표시된다.
export const appUpdatePollIntervalMs = 5 * 60_000;

// vite.config.ts 의 hatcheryBuildInfoPlugin 이 산출물 루트에 만드는 파일이다. nginx 가 `*.json` 을
// no-cache 로 주고, 여기서도 no-store 로 읽는다. 그래서 어떤 캐시도 이전 값을 반환하지 못한다.
const buildInfoPath = "/build-info.json";

/**
 * 현재 서버에 배포된 산출물의 빌드 식별자를 읽는다.
 *
 * 이 파일은 registry API 가 아니라 이 앱의 정적 파일이다. 그래서 `apiClient` 를 쓰지 않는다.
 * `apiClient` 는 `apiBaseUrl` 을 붙여 로컬 개발에서 다른 호스트로 요청한다. 인증 헤더도 필요 없다.
 * 응답이 비정상이거나 `build_id` 가 없으면 예외를 던진다. 호출자(PollingController)가 실패로
 * 처리하고, 안내는 표시되지 않는다.
 */
export async function fetchDeployedBuildId(): Promise<string> {
  const response = await fetch(buildInfoPath, { cache: "no-store" });
  if (!response.ok) {
    throw new Error(`build-info responded ${response.status}`);
  }
  const body: unknown = await response.json();
  const buildId = (body as { build_id?: unknown } | null)?.build_id;
  if (typeof buildId !== "string" || buildId === "") {
    throw new Error("build-info has no build_id");
  }
  return buildId;
}

/**
 * 배포된 빌드 식별자를 주기적으로 읽는다. 열린 탭은 이 값으로 자기 번들이 오래됐는지 안다(ZERG-463).
 *
 * 배너 구독이 폴링을 켜고 끈다. 배너는 로그인 셸에만 있다. 실패는 warn 으로만 기록한다.
 * 다음 경우에는 실패가 정상이고 사용자가 할 일이 없다:
 * - 이 파일이 없는 이전 산출물이 배포돼 있을 때.
 * - 산출물 없이 실행되는 환경.
 * PollingController 가 마지막 성공값을 유지한다. 그래서 한 번 오래됐다고 판정되면 이후 조회가
 * 실패해도 안내가 사라지지 않는다.
 */
export const appUpdateController = new PollingController<string>({
  load: () => fetchDeployedBuildId(),
  intervalMs: appUpdatePollIntervalMs,
  onLoadError: (error) => logger.warn("Failed to read deployed build id.", error),
});

// 서버의 식별자를 한 번이라도 받았고 그 값이 내 번들과 다르면 오래된 번들이다. 아직 받지
// 못했으면 알 수 없다고 판정한다(안내 없음). 롤백으로 서버가 내 식별자로 돌아오면 다시 최신이다.
export function isBundleStale(snapshot: PollingSnapshot<string>, currentBuildId: string): boolean {
  return snapshot.data !== null && snapshot.data !== currentBuildId;
}
